/* import __COLOCATED_TEMPLATE__ from './phone-office-hours.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: any;
    ruleset: $TSFixMe;
    numberConfiguration: FinVoiceNumberConfiguration;
    updateOfficeHours: (
      comparison: 'in' | 'nin' | undefined,
      schedule: string | undefined,
    ) => Promise<void>;
  };
}

export default class PhoneOfficeHours extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare finVoiceConfigService: FinVoiceConfigService;

  @tracked officeHoursScheduleComparison: 'in' | 'nin' | 'anytime' = 'anytime';
  @tracked selectedOfficeHoursSchedule?: string;
  @tracked officeHoursOptions = [
    {
      value: 'anytime',
      label: 'operator.fin.setup.tabs.setup-and-go-live.phone.office-hours.anytime',
    },
    {
      value: 'in',
      label: 'operator.fin.setup.tabs.setup-and-go-live.phone.office-hours.in-office-hours',
    },
    {
      value: 'nin',
      label: 'operator.fin.setup.tabs.setup-and-go-live.phone.office-hours.out-of-office-hours',
    },
  ];

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.loadOfficeHoursSchedules();
  }

  get officeHoursSchedules() {
    return this.finVoiceConfigService.officeHoursSchedules;
  }

  get savingDisabled() {
    return (
      !this.officeHoursSchedules?.length ||
      this.args.ruleset.isSaving ||
      this.numberConfiguration?.isSaving
    );
  }

  get isDisabled() {
    return !this.numberConfiguration;
  }

  get errorMessage() {
    return !this.officeHoursSchedules?.length
      ? this.intl.t(
          'operator.fin.setup.tabs.setup-and-go-live.phone.office-hours.error-no-office-hours',
        )
      : undefined;
  }

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get savedOfficeHoursScheduleComparison() {
    return this.numberConfiguration?.predicates?.officeHoursComparison || 'anytime';
  }

  get savedOfficeHoursSchedule() {
    return this.numberConfiguration?.predicates?.officeHoursSchedule;
  }

  get activeOfficeHoursScheduleDescription() {
    return this.officeHoursSchedules.find(
      (schedule) => schedule.value === this.selectedOfficeHoursSchedule,
    )?.description;
  }

  get activeOfficeHoursScheduleLabel() {
    let schedule = this.officeHoursSchedules.find(
      (schedule) => schedule.value === this.savedOfficeHoursSchedule,
    )?.text;

    let label: string;

    switch (this.savedOfficeHoursScheduleComparison) {
      case 'in':
        label =
          'operator.fin.setup.tabs.setup-and-go-live.phone.office-hours.in-office-hours-label';
        break;
      case 'nin':
        label =
          'operator.fin.setup.tabs.setup-and-go-live.phone.office-hours.out-of-office-hours-label';
        break;
      default:
        label = 'operator.fin.setup.tabs.setup-and-go-live.phone.office-hours.anytime-label';
        break;
    }

    return this.intl.t(label, {
      schedule,
    });
  }

  @action
  resetPredicate() {
    this.officeHoursScheduleComparison = this.savedOfficeHoursScheduleComparison;
    this.selectedOfficeHoursSchedule = this.savedOfficeHoursSchedule;
  }

  async loadOfficeHoursSchedules() {
    this.officeHoursScheduleComparison = this.savedOfficeHoursScheduleComparison;
    this.selectedOfficeHoursSchedule =
      this.savedOfficeHoursSchedule || this.finVoiceConfigService.getDefaultOfficeHoursSchedule();
  }

  @action
  updateOfficeHoursSchedule(value: string) {
    this.selectedOfficeHoursSchedule = value;
  }

  @action
  async updateOfficeHours() {
    if (this.savingDisabled) {
      return;
    }

    await this.args.updateOfficeHours(
      this.officeHoursScheduleComparison === 'anytime'
        ? undefined
        : this.officeHoursScheduleComparison,
      this.selectedOfficeHoursSchedule,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::WhoWillTalkToFin::PhoneOfficeHours': typeof PhoneOfficeHours;
  }
}
